import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import videoSourceService from '../services/videoSourceService';

// Get all video sources
export const getVideoSources = createAsyncThunk(
  'videoSources/getVideoSources',
  async (_, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('videoSourcesSlice: Calling videoSourceService.getAll()');
      }
      const videoSources = await videoSourceService.getAll();
      if (process.env.NODE_ENV === 'development') {
        console.log('videoSourcesSlice: Received video sources from service:', videoSources);
      }
      return videoSources;
    } catch (error) {
      console.error('videoSourcesSlice: Error fetching video sources:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get a single video source
export const getVideoSource = createAsyncThunk(
  'videoSources/getVideoSource',
  async (id, { rejectWithValue }) => {
    try {
      return await videoSourceService.getById(id);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Create a new video source
export const createVideoSource = createAsyncThunk(
  'videoSources/createVideoSource',
  async (videoSourceData, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('videoSourcesSlice: Creating video source with data:', videoSourceData);
      }
      const result = await videoSourceService.create(videoSourceData);
      if (process.env.NODE_ENV === 'development') {
        console.log('videoSourcesSlice: Video source created successfully:', result);
      }
      return result;
    } catch (error) {
      console.error('videoSourcesSlice: Error creating video source:', error);

      let errorMessage = 'Failed to create video source';

      // Handle specific authorization errors
      if (error.response?.status === 403) {
        errorMessage = 'Access denied: ADMIN role required to create video sources';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed: Please log in again';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return rejectWithValue(errorMessage);
    }
  }
);

// Delete a video source
export const deleteVideoSource = createAsyncThunk(
  'videoSources/deleteVideoSource',
  async (id, { rejectWithValue }) => {
    try {
      await videoSourceService.remove(id);
      return id;
    } catch (error) {
      let errorMessage = 'Failed to delete video source';

      // Handle specific authorization errors
      if (error.response?.status === 403) {
        errorMessage = 'Access denied: ADMIN role required to delete video sources';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed: Please log in again';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return rejectWithValue(errorMessage);
    }
  }
);

const initialState = {
  videoSources: [],
  currentVideoSource: null,
  loading: false,
  error: null,
  success: false,
};

const videoSourcesSlice = createSlice({
  name: 'videoSources',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    setCurrentVideoSource: (state, action) => {
      state.currentVideoSource = action.payload;
    },
    clearCurrentVideoSource: (state) => {
      state.currentVideoSource = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all video sources
      .addCase(getVideoSources.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVideoSources.fulfilled, (state, action) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('videoSourcesSlice: getVideoSources fulfilled with payload:', action.payload);
        }
        state.loading = false;
        state.videoSources = action.payload;
        if (process.env.NODE_ENV === 'development') {
          console.log('videoSourcesSlice: Updated video sources state:', state.videoSources);
        }
      })
      .addCase(getVideoSources.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get single video source
      .addCase(getVideoSource.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVideoSource.fulfilled, (state, action) => {
        state.loading = false;
        state.currentVideoSource = action.payload;
      })
      .addCase(getVideoSource.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create video source
      .addCase(createVideoSource.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createVideoSource.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.videoSources.push(action.payload);
      })
      .addCase(createVideoSource.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Delete video source
      .addCase(deleteVideoSource.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteVideoSource.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.videoSources = state.videoSources.filter(
          videoSource => videoSource.video_source_id !== action.payload
        );
      })
      .addCase(deleteVideoSource.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  },
});

export const { clearError, clearSuccess, setCurrentVideoSource, clearCurrentVideoSource } = videoSourcesSlice.actions;

export default videoSourcesSlice.reducer;
